import { useMutation, useQueryClient } from 'react-query';
import { putWithHeaders } from 'Core/utils/platformApi';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import useAlertStore, { clearAlertSelector, setErrorAlertSelector } from 'Core/stores/alert';
import { useTranslation } from 'react-i18next';
import { getUrlParam } from 'Core/utils/utils';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';

function transformPayload({
  customer_id,
  external_reference_id,
  purpose,
  provider,
  payment_option,
  request_increase,
}) {
  return {
    payment_id: getUrlParam('payment-id'),
    external_reference_id,
    purpose,
    payment_option,
    provider,
    payload: {
      customer_id,
      request_increase,
    },
  };
}

export default function useUpdateExistingAccount() {
  const { t } = useTranslation();
  const setErrorAlert = useAlertStore(setErrorAlertSelector);
  const clearAlert = useAlertStore(clearAlertSelector);
  const { data: paymentInfo } = useGetPaymentInfo();
  const queryClient = useQueryClient();

  return useMutation(
    ({ request_increase = false } = {}) =>
      putWithHeaders(
        `/financing/application/existing-account`,
        transformPayload({
          request_increase,
          customer_id: paymentInfo.customer_id,
          external_reference_id: paymentInfo.external_reference_id,
          purpose: paymentInfo.purpose,
          provider: paymentInfo.provider,
          payment_option: paymentInfo.payment_option,
        })
      ),
    {
      throwOnError: true,
      onSuccess(data) {
        clearAlert();
        queryClient.invalidateQueries(cacheKeys.financingPlatformApplication);
        queryClient.invalidateQueries(cacheKeys.financingNextStep);
        return data;
      },
      onError(error) {
        setErrorAlert(
          t(
            'patientfi.existingAccount.error.requestFailed',
            'We could not continue with your PatientFi account. Please try again.'
          ) + (error?.response?.data?.message ? ` (${error.response.data.message})` : '')
        );
      },
    }
  );
}
